import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import styles from './BundleGiftAOV.module.scss';
import messages from './messages';
import { priceToString } from '../../../utils';
import giftSvg from '../../../assets/images/svg/gift-icon-white.svg';
import bundleDetailsDownSvg from '../../../assets/images/svg/chevron-down-bundle-details.svg';
import VariantSelection from '../VariantSelection';
import {
  PILLOW_HEIGHT_NAME,
  PILLOW_SIZE_NAME,
  SHEET_TYPE,
} from '../../../utils/common/constants';
import BundleGiftAOVItem from './BundleGiftAOVItem';
import { VARIANT_SELECTION_SIZE } from '../VariantSelection/constants';
import { findDiscountTier } from '../../../utils/aov';
import { getProductsSpecsAndMaterials } from '../../../slices/pdp';
import AOVModalFooter from '../AOVModalFooter';

const BundleGiftAOV = ({
  title,
  children = null,
  description,
  bundleName,
  image,
  items,
  onCloseModal,
}) => {
  const { sanitize } = DOMPurify;
  const dispatch = useDispatch();
  const { formatMessage, formatNumber, locale } = useIntl();
  const [isBundleSelected, setIsBundleSelected] = useState(true);
  const [isBundleDetailsOpened, setIsBundleDetailsOpened] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [lineItems, setLineItems] = useState([]);

  const itemsAvailable = !items?.some(
    (item) => item?.gift?.variants?.length === 0,
  );
  const handleBundleGiftSelectionChange = useCallback((productKey, sku) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [productKey]: sku,
    }));
  }, []);

  useEffect(() => {
    if (isBundleSelected && itemsAvailable) {
      const defaultSelectedProducts = items.reduce((prev, curr) => {
        const masterVariant =
          curr.gift.variants.find((variant) => variant.masterVariant) ||
          curr.gift.variants[0];
        return {
          ...prev,
          [curr.gift.productKey]: masterVariant.sku,
        };
      }, {});
      setSelectedProducts(defaultSelectedProducts);
    } else {
      setSelectedProducts({});
    }
  }, [isBundleSelected, itemsAvailable, items]);

  useEffect(() => {
    const productKeys = items.map((item) => item.gift.productKey);
    dispatch(getProductsSpecsAndMaterials(productKeys));
  }, [items, dispatch]);

  const totalPrices = useMemo(() => {
    if (!items.some((item) => item.gift.variants.length === 0)) {
      return items.reduce(
        (acc, { gift: { variants, quantity } }) => {
          const discountTier = findDiscountTier(
            variants[0].discountsTier,
            +quantity,
          );
          return {
            price: {
              ...discountTier.price,
              centAmount: acc.price.centAmount + discountTier.price.centAmount,
            },
            discount: {
              ...discountTier.discount,
              centAmount:
                acc.discount.centAmount + discountTier.discount.centAmount,
            },
          };
        },
        {
          discount: {
            centAmount: 0,
            fractionDigits: 2,
            currencyCode: 'USD',
          },
          price: {
            centAmount: 0,
            fractionDigits: 2,
            currencyCode: 'USD',
          },
        },
      );
    }
    return null;
  }, [items]);

  const itemsSortedByQuantity = [...items].sort(
    (a, b) => b.gift.quantity - a.gift.quantity,
  );

  const renderSelectedBundleItemTitle = useCallback(
    (item) => {
      const quantity = item?.quantity || item?.gift?.quantity || 1;
      const selectedVariant = item.variants?.[0];
      const pillowSizeAttribute = selectedVariant?.attributes.find(
        (attr) => attr.name === PILLOW_SIZE_NAME,
      );
      let itemTitle =
        item?.title || item.productName?.[locale] || item.productName;
      if (pillowSizeAttribute) {
        itemTitle = `${pillowSizeAttribute.label} ${itemTitle}`;
      }
      if (quantity > 1) {
        itemTitle = `${quantity} ${itemTitle}s`;
      }
      return itemTitle;
    },
    [locale],
  );

  const totalPriceString = totalPrices
    ? priceToString({
        formatNumber,
        formatMessage,
        maximumFractionDigits: 0,
        price: {
          ...totalPrices.price,
          centAmount:
            totalPrices.price.centAmount + totalPrices.discount.centAmount,
        },
      })
    : null;
  const totalDiscountedPriceString = totalPrices
    ? priceToString({
        formatNumber,
        formatMessage,
        maximumFractionDigits: 0,
        price: totalPrices.discount,
      })
    : null;
  const isDiscounted = !!totalPrices?.discount?.centAmount;

  const hasPillowHeightAttribute = (item) =>
    !!item.gift.variants.find((variant) =>
      variant.attributes.find((attr) => attr.name === PILLOW_HEIGHT_NAME),
    );

  const skuToItemMap = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      item.gift.variants.forEach((variant) => {
        map[variant.sku] = item;
      });
    });
    return map;
  }, [items]);

  useEffect(() => {
    const selectedItems =
      Object.keys(selectedProducts).length > 0
        ? Object.values(selectedProducts)
            .map((productSku) => {
              const selectedItem = skuToItemMap[productSku];

              let selectedItemVariant = selectedItem;

              if (
                selectedItemVariant &&
                selectedItemVariant.gift.variants.length > 0
              ) {
                const matchedVariants = selectedItem.gift.variants.filter(
                  (variant) => variant.sku === productSku,
                );
                selectedItemVariant = {
                  ...selectedItem,
                  variants: matchedVariants,
                };
              }
              if (
                !selectedItemVariant?.variants ||
                selectedItemVariant?.variants?.length === 0
              ) {
                return null;
              }

              const discountTier = findDiscountTier(
                selectedItemVariant?.variants?.[0].discountsTier,
                +selectedItemVariant?.gift?.quantity || 1,
              );

              const productName =
                renderSelectedBundleItemTitle(selectedItemVariant);
              const itemSummary = {
                productKey: selectedItemVariant?.gift?.productKey,
                sku: selectedItemVariant?.variants?.[0]?.sku,
                productName,
                discountedPrice: discountTier?.discount,
                price: selectedItemVariant?.variants?.[0]?.price,
              };
              return itemSummary;
            })
            .filter(Boolean)
        : [];
    setLineItems(selectedItems.filter((item) => item !== null));
  }, [selectedProducts, skuToItemMap, renderSelectedBundleItemTitle]);

  return (
    <div className={styles.container}>
      <div className={styles['flyout-header']}>
        <div className={styles.title}>{title[locale]}</div>
        <button
          aria-label={formatMessage(messages.closeModal)}
          type='button'
          onClick={onCloseModal}
          data-testid='btn__close__modal'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onCloseModal();
            }
          }}
          id='button1'
        >
          <div className={styles['close-button']} />
        </button>
      </div>
      <div className={styles['promo-banner-block']}>{children}</div>
      <div
        className={classNames(styles['content-wrapper'], {
          [styles['content-wrapper--empty']]: !(
            Object.keys(selectedProducts).length > 0 || itemsAvailable
          ),
        })}
      >
        {(Object.keys(selectedProducts).length > 0 || itemsAvailable) && (
          <div className={styles.content}>
            <div
              className={styles.description}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitize(description[locale]),
              }}
            />
            <p className={styles['total-discount-value']}>
              {formatMessage(messages.totalDiscountPrice, {
                price: totalPriceString,
              })}
            </p>

            <div
              className={classNames(styles['bundle-container'], {
                [styles.selected]: isBundleSelected,
              })}
            >
              <button
                type='button'
                onClick={() => setIsBundleSelected((prev) => !prev)}
                className={classNames(styles['bundle-info-section'], {
                  [styles.select]: !isBundleSelected,
                })}
                aria-label={formatMessage(messages.selectBundle)}
                role='checkbox'
                aria-checked={isBundleSelected}
              >
                <img
                  src={image}
                  alt='Bundle Gift Img'
                  className={styles['bundle-image']}
                />
                <div className={styles['bundle-info']}>
                  <div className={styles['bundle-top-section']}>
                    <div className={styles['bundle-badge']}>
                      <img src={giftSvg} alt='Gift' />
                      <span>{formatMessage(messages.freeGift)}</span>
                    </div>
                    <div
                      className={classNames(styles['bundle-check-mark'], {
                        [styles.selected]: isBundleSelected,
                        [styles.select]: !isBundleSelected,
                      })}
                    />
                  </div>
                  <div className={styles['bundle-title']}>
                    {bundleName[locale]}
                  </div>
                  <ul className={styles['bundle-list']}>
                    {itemsSortedByQuantity.map((item) => (
                      <li key={item.gift.productKey}>
                        <div className={styles['bundle-item-check-mark']} />
                        <span>{renderSelectedBundleItemTitle(item?.gift)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className={styles['bundle-price']}>
                    <span
                      className={classNames(styles['bundle-total-price'], {
                        [styles.strikethrough]: isDiscounted,
                      })}
                    >
                      {totalPriceString}
                    </span>
                    {isDiscounted && (
                      <span className={styles['bundle-discounted-price']}>
                        {totalDiscountedPriceString}
                      </span>
                    )}
                  </div>
                </div>
              </button>

              <div
                className={classNames(styles['bundle-bottom-section'], {
                  [styles['bundle-details-opened']]: isBundleDetailsOpened,
                })}
              >
                {itemsAvailable &&
                  items.map((item) => (
                    <div key={item.gift.productKey}>
                      <VariantSelection
                        attributeTitlePrefix={
                          item.gift.productType.key === SHEET_TYPE
                            ? formatMessage(messages.sheet)
                            : undefined
                        }
                        productKey={item.gift.productKey}
                        variants={item.gift.variants}
                        defaultSelectedSKU={
                          selectedProducts[item.gift.productKey]
                        }
                        onSelectVariant={({ sku }) =>
                          handleBundleGiftSelectionChange(
                            item.gift.productKey,
                            sku,
                          )
                        }
                        size={VARIANT_SELECTION_SIZE.SMALL}
                        hidePillowSize
                        isDropdown={hasPillowHeightAttribute(item)}
                      />
                    </div>
                  ))}
                <button
                  type='button'
                  className={classNames(styles['bundle-details-toggle-button'])}
                  onClick={() => setIsBundleDetailsOpened((prev) => !prev)}
                >
                  <span>{formatMessage(messages.bundleDetails)}</span>
                  <img src={bundleDetailsDownSvg} alt='Toggle Bundle Details' />
                </button>

                <div className={styles['bundle-items']}>
                  {selectedProducts &&
                    items.map((item) => (
                      <Fragment key={item.gift.productKey}>
                        <BundleGiftAOVItem
                          item={item}
                          selectedSku={selectedProducts[item.gift.productKey]}
                          showBundleItems
                        />
                      </Fragment>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <AOVModalFooter lineItems={lineItems} />
      </div>
    </div>
  );
};

BundleGiftAOV.propTypes = {
  title: PropTypes.object.isRequired,
  children: PropTypes.node,
  description: PropTypes.object.isRequired,
  bundleName: PropTypes.object.isRequired,
  image: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onCloseModal: PropTypes.func.isRequired,
};

export default BundleGiftAOV;
